import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { mfaKeys } from './useMFA';

// ============ Types ============

export type OtpMfaMethod = 'email' | 'sms';

export interface OtpMfaEnrollment {
  method: OtpMfaMethod;
  destination: string; // email or phone number
}

// ============ Hooks ============

/**
 * Hook to send OTP for MFA verification (after password login)
 * Uses custom edge functions since Supabase doesn't natively support Email/SMS as MFA factors
 */
export function useSendMfaOtp() {
  const { t } = useTranslation('common');

  return useMutation({
    mutationFn: async ({ method, destination }: { method: OtpMfaMethod; destination: string }) => {
      const { data, error } = await supabase.functions.invoke('send-mfa-otp', {
        body: { method, destination },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      return { method, destination, expiresAt: data.expiresAt };
    },
    onSuccess: ({ method, destination }) => {
      const maskedDestination = method === 'email' 
        ? maskEmail(destination)
        : maskPhone(destination);
      
      toast.success(
        t('mfa.otpSent', 'Verification code sent'),
        { description: t('mfa.otpSentTo', { destination: maskedDestination, defaultValue: 'Code sent to {{destination}}' }) }
      );
    },
    onError: (error: any) => {
      console.error('MFA OTP send error:', error);
      toast.error(t('mfa.otpSendError', 'Failed to send verification code'));
    },
  });
}

/**
 * Hook to verify OTP for MFA challenge
 */
export function useVerifyMfaOtp() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ method, code }: { method: OtpMfaMethod; code: string }) => {
      const { data, error } = await supabase.functions.invoke('verify-mfa-otp', {
        body: { method, code },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      // Update last verified timestamp in enrollment status
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase
          .from('mfa_enrollment_status')
          .update({ last_verified_at: new Date().toISOString() })
          .eq('user_id', user.id);

        // Log session event
        await supabase.rpc('log_session_event', {
          p_user_id: user.id,
          p_event_type: 'mfa_success',
          p_metadata: { method },
        });
      }
      
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mfaKeys.all });
      toast.success(t('mfa.verifySuccess', 'Verification successful'));
    },
    onError: async (error: any) => {
      console.error('MFA OTP verification error:', error);
      
      // Log failed attempt
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.rpc('log_session_event', {
          p_user_id: user.id,
          p_event_type: 'mfa_failure',
          p_metadata: { method: 'otp' },
        });
      }

      const message = error.message?.includes('expired')
        ? t('mfa.otpExpired', 'Code expired. Please request a new one.')
        : t('mfa.invalidOtp', 'Invalid verification code');
      toast.error(message);
    },
  });
}

/**
 * Hook to enroll in OTP-based MFA (Email or SMS)
 */
export function useEnrollOtpMfa() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ method, destination, code }: { method: OtpMfaMethod; destination: string; code?: string }) => {
      const { data, error } = await supabase.functions.invoke('enroll-otp-mfa', {
        body: { method, destination, code },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      return { method, destination };
    },
    onSuccess: ({ method }) => {
      // Invalidate all MFA-related queries to refresh UI
      queryClient.invalidateQueries({ queryKey: mfaKeys.all });
      queryClient.invalidateQueries({ queryKey: ['unified-mfa'] });
      queryClient.invalidateQueries({ queryKey: ['mfa-factors'] });
      queryClient.invalidateQueries({ queryKey: ['mfa-status'] });
      const methodName = method === 'email' ? 'Email' : 'SMS';
      toast.success(t('mfa.enrollSuccess', `${methodName} verification enabled`));
    },
    onError: (error: any) => {
      console.error('OTP MFA enrollment error:', error);
      toast.error(t('mfa.enrollError', 'Failed to enable verification'));
    },
  });
}

/**
 * Hook to unenroll from OTP-based MFA
 */
export function useUnenrollOtpMfa() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (method: OtpMfaMethod) => {
      const { data, error } = await supabase.functions.invoke('unenroll-otp-mfa', {
        body: { method },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      
      return { method };
    },
    onSuccess: ({ method }) => {
      queryClient.invalidateQueries({ queryKey: mfaKeys.all });
      const methodName = method === 'email' ? 'Email' : 'SMS';
      toast.success(t('mfa.unenrollSuccess', `${methodName} verification disabled`));
    },
    onError: (error: any) => {
      console.error('OTP MFA unenroll error:', error);
      toast.error(t('mfa.unenrollError', 'Failed to disable verification'));
    },
  });
}

// ============ Helpers ============

function maskEmail(email: string): string {
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) return email;
  
  const visibleChars = Math.min(3, localPart.length);
  const masked = localPart.slice(0, visibleChars) + '***';
  return `${masked}@${domain}`;
}

function maskPhone(phone: string): string {
  if (phone.length < 6) return phone;
  const lastFour = phone.slice(-4);
  return `***${lastFour}`;
}
